import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  SUPPORT_CATEGORY_LABELS,
  SUPPORT_URGENCE_MAP,
  SUPPORT_STATUS_MAP,
} from '../components/support/supportConstants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR') + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch (_) {
    return iso;
  }
};

/** Page "Mes signalements" — accessible via le menu profil et les liens email. */
const SupportPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reopenId, setReopenId] = useState(null);
  // lot DJ — /support?ticket={id} (sans reopen) : le signalement est déplié et mis en évidence
  const [ticketEnEvidence, setTicketEnEvidence] = useState(
    params.get('ticket') && params.get('reopen') !== '1' ? params.get('ticket') : null);
  useEffect(() => {
    if (!ticketEnEvidence) return undefined;
    const minuteur = setTimeout(() => setTicketEnEvidence(null), 3000);
    return () => clearTimeout(minuteur);
  }, [ticketEnEvidence]);
  const [reopenMsg, setReopenMsg] = useState('');
  const [busy, setBusy] = useState(false);
  // Horodatage de la DERNIÈRE lecture, figé à l'arrivée : c'est lui qui départage le nouveau
  // de l'ancien. On le garde avant d'appeler `mark-read`, sinon tout serait « déjà lu ».
  const [luAvant, setLuAvant] = useState(null);
  const [filtre, setFiltre] = useState('tous'); // 'tous' | 'nouveautes' | 'en_cours'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/support/my-tickets`);
      const liste = data.tickets || [];
      setLuAvant((prev) => prev ?? (liste.reduce(
        (max, t) => (t.user_read_at && t.user_read_at > max ? t.user_read_at : max), '') || ''));
      setTickets(liste);
      // Consulter la page vaut lecture : la pastille du menu utilisateur retombe à zéro.
      axios.post(`${API}/support/mark-read`).catch(() => {});
    } catch (_) {
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Lien email "Rouvrir" : /support?ticket=X&reopen=1
  useEffect(() => {
    if (params.get('reopen') === '1' && params.get('ticket')) {
      setReopenId(params.get('ticket'));
    }
  }, [params]);

  const confirmResolution = async (id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/support/tickets/${id}/confirm`);
      toast.success('Merci, ticket fermé.');
      load();
    } catch (_) {
      toast.error('Action impossible.');
    } finally {
      setBusy(false);
    }
  };

  const submitReopen = async (id) => {
    if (!reopenMsg.trim()) {
      toast.error('Un commentaire est obligatoire pour rouvrir.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/support/tickets/${id}/reopen`, { message: reopenMsg.trim() });
      toast.success('Ticket rouvert.');
      setReopenId(null);
      setReopenMsg('');
      load();
    } catch (_) {
      toast.error('Réouverture impossible.');
    } finally {
      setBusy(false);
    }
  };

  // Fil unique par ticket : réponses visibles + changements de statut, triés dans le temps.
  const construireFil = (t) => {
    const evts = (t.comments || []).map((c) => ({
      cle: c.id, type: 'message', at: c.created_at,
      auteur: c.author_role === 'support' ? 'Support' : 'Vous',
      support: c.author_role === 'support',
      texte: c.message,
    }));
    (t.statut_events || []).forEach((e, i) => {
      const utilisateur = ['user_confirm', 'email_confirm', 'user_reopen'].includes(e.via);
      evts.push({
        cle: `st-${i}`, type: 'statut', at: e.at,
        auteur: utilisateur ? 'Vous' : 'Support', support: !utilisateur,
        texte: `Statut : ${(SUPPORT_STATUS_MAP[e.to] || { label: e.to }).label}`,
      });
    });
    return evts.sort((a, b) => String(a.at).localeCompare(String(b.at)));
  };

  const estNouveau = (evt) => Boolean(evt.support && luAvant !== null && String(evt.at) > luAvant);

  const enrichis = tickets
    .map((t) => {
      const fil = construireFil(t);
      return { ...t, fil, nonLus: fil.filter(estNouveau).length };
    })
    // Ce qui est nouveau d'abord, puis le plus récent.
    .sort((a, b) => (b.nonLus > 0) - (a.nonLus > 0)
      || String(b.created_at).localeCompare(String(a.created_at)));

  const totalNonLus = enrichis.reduce((n, t) => n + t.nonLus, 0);
  const visibles = enrichis.filter((t) => (
    filtre === 'nouveautes' ? t.nonLus > 0
      : filtre === 'en_cours' ? ['nouveau', 'en_cours', 'rouvert', 'resolu'].includes(t.statut)
        : true));

  return (
    <div className="bg-slate-50 min-h-full">
      {/* En-tête de la page (la barre latérale et la barre du haut viennent de la coquille) */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition"
            title="Retour"
            data-testid="support-page-back"
          >
            <i className="bi bi-arrow-left text-lg"></i>
          </button>
          <div className="flex items-center gap-2">
            <i className="bi bi-life-preserver text-slate-700"></i>
            <h1 className="font-['Outfit'] text-xl font-bold text-slate-800">Mes signalements</h1>
            {totalNonLus > 0 && (
              <span
                data-testid="support-page-unread"
                className="ml-1 px-2 py-0.5 rounded-full bg-red-600 text-white text-[11px] font-bold"
              >
                {totalNonLus} nouveau{totalNonLus > 1 ? 'x' : ''}
              </span>
            )}
          </div>
          <div className="w-full md:w-auto md:ml-auto flex items-center gap-1 overflow-x-auto md:overflow-visible">
            {[
              { v: 'tous', l: 'Tous' },
              { v: 'nouveautes', l: 'Nouveautés' },
              { v: 'en_cours', l: 'En cours' },
            ].map(({ v, l }) => (
              <button
                key={v}
                onClick={() => setFiltre(v)}
                data-testid={`support-filtre-${v}`}
                className={`px-3 h-10 md:h-auto md:py-1.5 shrink-0 whitespace-nowrap rounded-lg text-xs font-semibold transition ${
                  filtre === v ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6" data-testid="support-page">
        {loading ? (
          <div className="text-center py-16 text-slate-400">
            <i className="bi bi-arrow-repeat animate-spin text-3xl"></i>
          </div>
        ) : tickets.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <i className="bi bi-inbox text-4xl text-slate-300"></i>
            <p className="text-slate-500 mt-3">Vous n'avez aucun signalement.</p>
          </div>
        ) : tickets.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <i className="bi bi-inbox text-4xl text-slate-300"></i>
            <p className="text-slate-500 mt-3">Vous n'avez aucun signalement.</p>
          </div>
        ) : visibles.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
            <i className="bi bi-check2-circle text-3xl text-emerald-500"></i>
            <p className="text-slate-500 mt-3">Rien de nouveau ici.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {visibles.map((t) => {
              const urg = SUPPORT_URGENCE_MAP[t.urgence] || SUPPORT_URGENCE_MAP.normal;
              const st = SUPPORT_STATUS_MAP[t.statut] || { label: t.statut, chip: 'bg-slate-100 text-slate-600' };
              const derniere = t.fil.length ? t.fil[t.fil.length - 1].at : t.created_at;
              return (
                <div
                  key={t.id}
                  className={`bg-white rounded-xl shadow-sm p-4 border transition-shadow text-left ${
                    t.nonLus > 0
                      ? 'border-red-200 border-l-4 border-l-red-500 shadow-md'
                      : 'border-slate-200'
                  } ${ticketEnEvidence === t.id ? 'ring-2 ring-blue-400' : ''}`}
                  data-testid={`support-my-ticket-${t.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-sm font-semibold text-slate-800">
                          {SUPPORT_CATEGORY_LABELS[t.categorie] || t.categorie}
                        </span>
                        {t.numero && <span className="text-xs text-slate-400 font-mono">{t.numero}</span>}
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${st.chip}`}>{st.label}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${urg.chip}`}>{urg.label}</span>
                        {t.nonLus > 0 && (
                          <span
                            data-testid={`support-ticket-unread-${t.id}`}
                            className="px-2 py-0.5 rounded-full bg-red-600 text-white text-[11px] font-bold"
                          >
                            {t.nonLus} nouveau{t.nonLus > 1 ? 'x' : ''}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 whitespace-pre-wrap break-words">{t.description}</p>
                      <p className="text-xs text-slate-400 mt-1.5">
                        Signalé le {fmtDate(t.created_at)}
                        {derniere !== t.created_at && <> · dernière activité le {fmtDate(derniere)}</>}
                      </p>
                    </div>
                  </div>

                  {/* Fil : réponses du support et changements de statut, du plus ancien au plus récent */}
                  {t.fil.length > 0 && (
                    <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
                      {t.fil.map((e) => {
                        const nouveau = estNouveau(e);
                        return (
                          <div
                            key={e.cle}
                            className={`rounded-lg px-3 py-2 text-sm text-left ${
                              nouveau ? 'bg-red-50 ring-1 ring-red-200' : 'bg-slate-50'
                            }`}
                            data-testid={`support-event-${t.id}-${e.cle}`}
                          >
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-xs font-semibold ${e.support ? 'text-blue-700' : 'text-slate-500'}`}>
                                {e.auteur}
                              </span>
                              <span className="text-[11px] text-slate-400">{fmtDate(e.at)}</span>
                              {nouveau && (
                                <span className="px-1.5 py-0.5 rounded bg-red-600 text-white text-[10px] font-bold tracking-wide">
                                  NOUVEAU
                                </span>
                              )}
                            </div>
                            <p className={`mt-0.5 whitespace-pre-wrap break-words ${
                              e.type === 'statut' ? 'text-slate-500 italic' : 'text-slate-700'
                            }`}>
                              {e.texte}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Actions pour ticket résolu */}
                  {t.statut === 'resolu' && (
                    <div className="mt-3 border-t border-slate-100 pt-3">
                      {reopenId === t.id ? (
                        <div className="space-y-2">
                          <textarea
                            value={reopenMsg}
                            onChange={(e) => setReopenMsg(e.target.value)}
                            rows={2}
                            placeholder="Expliquez pourquoi le problème persiste…"
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/40 outline-none resize-none"
                            data-testid={`support-reopen-msg-${t.id}`}
                          />
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => { setReopenId(null); setReopenMsg(''); }}
                              className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-sm hover:bg-slate-50"
                            >
                              Annuler
                            </button>
                            <button
                              onClick={() => submitReopen(t.id)}
                              disabled={busy}
                              className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium disabled:opacity-50"
                              data-testid={`support-reopen-submit-${t.id}`}
                            >
                              Rouvrir le ticket
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => setReopenId(t.id)}
                            className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-sm hover:bg-slate-50"
                            data-testid={`support-reopen-btn-${t.id}`}
                          >
                            <i className="bi bi-arrow-counterclockwise mr-1"></i> Le problème persiste
                          </button>
                          <button
                            onClick={() => confirmResolution(t.id)}
                            disabled={busy}
                            className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50"
                            data-testid={`support-confirm-btn-${t.id}`}
                          >
                            <i className="bi bi-check-lg mr-1"></i> C'est résolu
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default SupportPage;
